/**
 * The bug-report payload, and the replay that proves it is one (task 13.8).
 *
 * The claim the spec makes is strong: a payload replayed through the engine
 * reproduces the run's state and trace log *exactly*. That is only true if the
 * payload carries every input `createGame` consulted, not just the obvious
 * ones — the seed and the action log reproduce nothing on their own if the run
 * was opened at a different phase budget or with a perk equipped.
 *
 * So the payload is the complete set of arguments to `openGame` plus the moves:
 *
 *     packId · packVersion        which content
 *     engineVersion · stateVersion which build
 *     profileId                    which difficulty
 *     parPhases · equippedPerkIds  what the profile derived from
 *     seed                         which draw
 *     actions[]                    what was done
 *
 * Everything in it is an identifier, a number, or an action. No player-visible
 * text, no state, nothing about the device — a payload is something a person
 * pastes into an issue, so it has to be readable and it has to be dull.
 */
import { openGame, type Action, type GameState, type Trace } from '@managing-up/engine';
import type { CareerPerks, LevelPackage, Profile } from '@managing-up/schema';
import type { SavedRun } from './types.js';

export const DIAGNOSTIC_VERSION = 1;

export interface DiagnosticPayload {
  readonly reportVersion: number;
  readonly packId: string;
  readonly packVersion: string;
  readonly engineVersion: string;
  readonly stateVersion: number;
  /** The difficulty profile the run was opened at. */
  readonly profileId: string;
  readonly seed: string;
  /** The solver par the phase budget was derived from, where one was known. */
  readonly parPhases: number | null;
  readonly equippedPerkIds: readonly string[];
  readonly actions: readonly Action[];
}

/**
 * The payload for a saved run.
 *
 * Note that `profileId` is the *difficulty* profile, not the save profile: the
 * save profile's identity is a local detail that reproduces nothing, and its
 * name is the player's.
 */
export function diagnosticFrom(run: SavedRun): DiagnosticPayload {
  return {
    reportVersion: DIAGNOSTIC_VERSION,
    packId: run.packId,
    packVersion: run.packVersion,
    engineVersion: run.engineVersion,
    stateVersion: run.stateVersion,
    // The profile in force now, after any mid-mission switch — which is what a
    // replay has to open at for the numbers to match.
    profileId: run.state.settings.profileId,
    seed: run.seed,
    parPhases: run.parPhases,
    equippedPerkIds: run.equippedPerkIds,
    actions: run.history,
  };
}

/** The payload as a person pastes it: pretty-printed, newline-terminated. */
export function diagnosticText(payload: DiagnosticPayload): string {
  return `${JSON.stringify(payload, null, 2)}\n`;
}

/** Rejects a payload that could not have come from this application. */
export function parseDiagnostic(text: string): DiagnosticPayload {
  let raw: unknown;
  try {
    raw = JSON.parse(text) as unknown;
  } catch {
    throw new Error('That is not valid JSON.');
  }
  const payload = raw as Partial<DiagnosticPayload> | null;
  if (!payload || typeof payload !== 'object' || !Array.isArray(payload.actions)) {
    throw new Error('That is not a Managing Up diagnostic report.');
  }
  for (const field of ['packId', 'packVersion', 'seed', 'profileId'] as const) {
    if (typeof payload[field] !== 'string' || payload[field] === '') {
      throw new Error(`That diagnostic report is missing the \`${field}\` field.`);
    }
  }
  return payload as DiagnosticPayload;
}

export interface ReplayResult {
  readonly state: GameState;
  /** Every resolution the replay produced, in order. */
  readonly traces: readonly Trace[];
  /**
   * Actions the engine refused. Empty for a faithful replay; anything in it
   * means the payload and the package or the build disagree, and the state
   * below is not the state that was reported.
   */
  readonly refused: readonly Action[];
}

export interface ReplayOptions {
  /** Needed only when the payload names equipped perks. */
  readonly perks?: CareerPerks;
  /** From the profiles document, as `createGame` takes it. */
  readonly bandwidthCapacity?: number;
}

/**
 * Replays a payload through the engine.
 *
 * The refusal list is the point of the return shape. A replay that silently
 * skipped an illegal action would produce a plausible state that never
 * happened, and the person reading the report would debug the wrong run.
 */
export function replayDiagnostic(
  pack: LevelPackage,
  profile: Profile,
  payload: DiagnosticPayload,
  options: ReplayOptions = {},
): ReplayResult {
  const game = openGame(pack, profile, payload.seed, {
    ...(payload.parPhases === null ? {} : { parPhases: payload.parPhases }),
    ...(options.perks ? { perks: options.perks, equipped: payload.equippedPerkIds } : {}),
    ...(options.bandwidthCapacity === undefined
      ? {}
      : { bandwidthCapacity: options.bandwidthCapacity }),
  });

  let state = game.state;
  const traces: Trace[] = [];
  const refused: Action[] = [];

  for (const action of payload.actions) {
    const before = state;
    const result = game.reduce(state, action);
    if (result.state === before && result.effects.length === 0) {
      refused.push(action);
      continue;
    }
    state = result.state;
    traces.push(...result.traces);
  }

  return { state, traces, refused };
}
